import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {clearForm} from '../../redux/slice/loginSlice';
import './LoginPage.css';
import {ChangePassword} from '../../services/AuthService';
import * as Constants from './LoginPageConstant';
import {RootState} from '../../redux/Store';
import {SignOut} from '../../services/AuthService';
import {useNavigate} from 'react-router-dom';
import {updateAuthForm} from '../../redux/slice/loginSlice';

const AfterLoginChangePassword: React.FC = () => {
  const dispatch = useDispatch();

  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<string>('error');
  const [loading, setLoading] = useState<boolean>(false);
  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);
  const [reasonCode, setReasonCode] = useState<number>(4);
  const deviceId = localStorage.getItem('deviceId');
  const deviceType = localStorage.getItem('deviceType');
  const navigate = useNavigate();

  useEffect(() => {
    if (validationMessage) {
      const timer = setTimeout(() => {
        setValidationMessage('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [validationMessage]);

  const handleLogout = async () => {
    localStorage.removeItem('token');
    sessionStorage.clear();
    dispatch(updateAuthForm({token: null, isSuccessful: false}));

    const response = await SignOut({
      Userid: Number(loggedInUserid),
      loginType: 'LogOut',
      deviceId: String(deviceId),
      deviceName: String(deviceType),
    });

    if (response.isSuccess) {
      navigate('/', {replace: true});
    }
  };
  const handlePasswordUpdate = async () => {
    const validationMessages: string[] = [];
    const passwordCriteriaRegex =
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\W_]{8,}$/;

    if (!currentPassword) {
      validationMessages.push(Constants.validationMessages.currentPassword);
    }
    if (!newPassword) {
      validationMessages.push(Constants.validationMessages.newPassword);
    } else if (!passwordCriteriaRegex.test(newPassword)) {
      validationMessages.push(Constants.validationMessages.passwordError);
    }
    if (!confirmPassword) {
      validationMessages.push(Constants.validationMessages.confirmpassword);
    } else if (newPassword !== confirmPassword) {
      validationMessages.push(Constants.validationMessages.passwordMismatch);
    }

    if (validationMessages.length > 0) {
      setValidationMessage(validationMessages.join(' '));
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      const response = await ChangePassword({
        Userid: Number(loggedInUserid),
        OldPassword: currentPassword,
        NewPassword: newPassword,
        ReasonCode: reasonCode,
      });
      if (response.statusCode == '1') {
        setValidationMessage(Constants.validationMessages.passwordChanged);
        setMessageType('success');
        setTimeout(() => {
          dispatch(clearForm());      
        handleLogout();
        }, 3000); 
      } else if (response.statusCode == '2') {
        setValidationMessage(Constants.validationMessages.invalidUser);
        setMessageType('error');
      } else if (response.statusCode == '3') {
        setValidationMessage(
          Constants.validationMessages.currentPasswordMismatch,
        );
        setMessageType('error');
      } else if (response.statusCode == '4') {
        setValidationMessage(
          Constants.validationMessages.currentNewPasswordSame,
        );
        setMessageType('error');
      } else if (response.statusCode == '5') {
        setValidationMessage(Constants.validationMessages.reusedPassword);
        setMessageType('error');
      }
    } catch (error) {
      setValidationMessage(
        'An unexpected error occurred while processing your request.',
      );
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {validationMessage && (
        <div
          className={`alert d-flex justify-content-between align-items-start mx-auto ${
            messageType === 'success' ? 'alert-success' : 'alert-danger'
          }`}
          style={{minWidth: '320px'}}
        >
          <div className='me-auto'>
            <div>
              {validationMessage.split('\n').map((msg, index) => (
                <span key={index} style={{display: 'block'}}>
                  {msg}
                </span>
              ))}
            </div>
          </div>
          <button
            type='button'
            className='btn-close bg-dark ms-3 p-1'
            aria-label='Close'
            onClick={() => setValidationMessage('')}
          >
            <span className='material-icons'>close</span>
          </button>
        </div>
      )}
      <div className='form-floating mb-3'>
        <input
          type={showCurrentPassword ? 'text' : 'password'}
          className='form-control'
          id='currentPassword'
          placeholder='Current Password'
          value={currentPassword}
          maxLength={30}
          required
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <label htmlFor='currentPassword'>Current Password</label>
        <span
          className='toggle-password toggle-password-inner'
          onClick={() => setShowCurrentPassword((prev) => !prev)}
        >
          <span className='material-icons'>
            {showCurrentPassword ? 'visibility' : 'visibility_off'}
          </span>
        </span>
      </div>

      <div className='form-floating mb-3'>
        <input
          type={showNewPassword ? 'text' : 'password'}
          className='form-control'
          id='newPassword'
          placeholder='New Password'
          value={newPassword}
          required
          maxLength={30}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <label htmlFor='newPassword'>New Password</label>
        <span
          className='toggle-password toggle-password-inner'
          onClick={() => setShowNewPassword((prev) => !prev)}
        >
          <span className='material-icons'>
            {showNewPassword ? 'visibility' : 'visibility_off'}
          </span>
        </span>
      </div>

      <div className='form-floating mb-3'>
        <input
          type={showConfirmPassword ? 'text' : 'password'}
          className='form-control'
          id='confirmPassword'
          placeholder='Confirm Password'
          value={confirmPassword}
          maxLength={30}
          required
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <label htmlFor='confirmPassword'>Confirm Password</label>
        <span
          className='toggle-password toggle-password-inner'
          onClick={() => setShowConfirmPassword((prev) => !prev)}
        >
          <span className='material-icons'>
            {showConfirmPassword ? 'visibility' : 'visibility_off'}
          </span>
        </span>
      </div>

      <div className='passwordcriteria'>
        <p className='mb-0'>Password Must:</p>
        <ul>
          <li>
            <span className='material-icons text-success fs-6'>check</span>{' '}
            Include lower and upper character
          </li>
          <li>
            <span className='material-icons text-success fs-6'>check</span>{' '}
            Include at least 1 number or symbol
          </li>
          <li>
            <span className='material-icons text-success fs-6'>check</span> Be
            at least 8 characters long
          </li>
          <li>
            <span className='material-icons text-success fs-6'>check</span>{' '}
            Match in both fields
          </li>
        </ul>
      </div>

      <div className='text-center'>
        <button
          type='button'
          className='btn btn-primary'
          onClick={handlePasswordUpdate}
          disabled={loading}
        >
          Submit
        </button>
      </div>

      {/* {validationMessage && (
  <div className={`alert alert-${messageType === 'error' ? 'danger' : 'success'} mt-2`}>
    {validationMessage}
  </div>
)} */}
    </>
  );
};

export default AfterLoginChangePassword;
